<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="KDDI GPU Cloud VNコネクト一覧"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <!-- 回線情報 -->
            <div>
              <h3>回線情報</h3>
            </div>
            <v-row class="my-1 ml-3">
              <v-col cols="4">
                <app-label-group
                  label="回線番号"
                  :value="vnConnectList.enumber"
                />
              </v-col>
              <v-col cols="4">
                <app-label-group
                  label="アクセス回線メニュー"
                  :value="
                    $filter.enumTo(
                      vnConnectList.accessType,
                      'cloudVn_accessType'
                    ) +
                    '(' +
                    $filter.bandwidthTo(accessLineMenuBandwidth, 'Mbps') +
                    ')'
                  "
                />
              </v-col>
              <v-col cols="4">
                <app-label-group
                  label="事業所名"
                  :value="vnConnectList.office"
                />
              </v-col>
            </v-row>

            <!-- クラウド情報 -->
            <h3>クラウド情報</h3>
            <v-row class="mt-1 ml-3">
              <v-col cols="4">
                <app-label-group label="種別" value="KDDI GPU Cloud" />
              </v-col>
              <v-col cols="4">
                <app-label-group label="帯域">
                  <template #value>
                    <span class="ml-3"
                      >{{
                        $filter.bandwidthTo(vnConnectList.bandwidth, "Mbps")
                      }}(設定可能帯域:</span
                    >
                    <span
                      :class="{
                        'text-error': remainingBandwidth.value < 0,
                      }"
                      >{{
                        $filter.bandwidthTo(remainingBandwidth, "Mbps")
                      }}</span
                    >)
                  </template>
                </app-label-group>
              </v-col>
            </v-row>

            <!-- VNコネクト情報 -->
            <div class="d-flex justify-space-between mb-4">
              <div class="d-flex align-end">
                <h3>VNコネクト情報</h3>
              </div>
              <div class="d-flex ga-2">
                <v-btn color="primary" @click="register" v-permission.config
                  >追加</v-btn
                >
                <v-btn
                  color="primary"
                  @click="modifyStatus(true)"
                  v-permission.config
                  >有効</v-btn
                >
                <v-btn
                  color="primary"
                  @click="modifyStatus(false)"
                  v-permission.config
                  >無効</v-btn
                >
              </div>
            </div>
            <v-alert
              v-if="alertMsg"
              class="my-2 text-left"
              :text="alertMsg"
            ></v-alert>
            <div v-permission.config class="text-left">
              <v-alert v-if="retry" class="my-2 bg-error"
                >{{
                  $filter.message("vn_connect_has_wnumber", {
                    wnumber: wnumberSelected,
                  })
                }}
                <a
                  class="text-primary text-decoration-underline cursor-pointer"
                  @click="retryVnConnect()"
                  >再実行はこちら</a
                >
              </v-alert>
              <v-alert v-if="pendingDelete" class="my-2 bg-error"
                >{{
                  $filter.message("vn_connect_has_pending_delete", {
                    wnumber: wnumberSelectedPendingDelete,
                  })
                }}
                <a
                  class="text-primary text-decoration-underline cursor-pointer"
                  @click="pendingDeleteVnConnect()"
                  >削除はこちら</a
                >
              </v-alert>
              <v-alert v-if="!retry && !pendingDelete && error" class="my-2">
                <span>{{ $filter.message("vn_connect_has_error") }}</span>
                <template
                  v-for="(wnumber, index) in wnumberSelectedError"
                  :key="wnumber"
                >
                  <a
                    class="text-primary text-decoration-underline cursor-pointer"
                    @click="selectTable(wnumber)"
                  >
                    {{ wnumber }}
                  </a>
                  <span
                    v-if="index < wnumberSelectedError.length - 1"
                    class="text-primary"
                  >
                    ,
                  </span>
                </template>
              </v-alert>
            </div>

            <app-table
              primary-key="cloudVnConnectAct.wnumber"
              :fields="vnConnects.fields"
              :items="vnConnectList.cloudVnConnectsList"
              :selectable="selectable"
              v-model="vnConnects.selected"
              :hover="true"
              v-model:table-sort-by="vnConnects.sortBy"
              @click:row="showConnectionDetails"
              :td-class="cellClass"
            >
              <template #cell(wnumber)="{ item }">
                {{ item.cloudVnConnectAct.wnumber }}<br />
                {{ item.cloudVnConnectSby.wnumber }}
              </template>
              <template #cell(vnConnectName)="{ item }">
                {{ item.cloudVnConnectAct.vnConnectName }}<br />
                {{ item.cloudVnConnectSby.vnConnectName }}
              </template>
              <template #cell(isVnConnectStatus)="{ item }">
                {{
                  $filter.enumTo(
                    item.cloudVnConnectAct.isVnConnectStatus,
                    "enable"
                  )
                }}<br />
                {{
                  $filter.enumTo(
                    item.cloudVnConnectSby.isVnConnectStatus,
                    "enable"
                  )
                }}
              </template>
              <template #cell(mNumber)="{ item }">
                {{ item.cloudVnConnectCommon.mNumber }}
              </template>
              <template #cell(bandwidth)="{ item }">
                {{ item.cloudVnConnectCommon.bandwidthType }}({{
                  $filter.bandwidthTo(item.cloudVnConnectCommon.bandwidth)
                }})
              </template>
              <template #cell(routingType)="{ item }">
                {{ item.cloudVnConnectCommon.routingType }}
              </template>
              <template #cell(provStatus)="{ item }">
                <span
                  :class="statusColor(item.cloudVnConnectCommon.provStatus)"
                  style="white-space: pre-wrap"
                  class="d-flex align-center"
                >
                  {{
                    $filter.enumTo(
                      item.cloudVnConnectCommon.provStatus,
                      "gpuStatus"
                    )
                  }}
                </span>
              </template>
              <template #cell(description)="{ item }">
                {{
                  $filter.emptyTo(item.cloudVnConnectCommon.description, "-")
                }}
              </template>
            </app-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { CloudVnGpuList } from "@/apis/CloudVnGpuApi";
import type { AppTableHeader, SortItem } from "@/components/AppTable.vue";
import MultiCloudGpuVnConnectionReference from "@/modals/multicloud/gpu/MultiCloudGpuVnConnectionReference.vue";
import MultiCloudVNConnectStatusModify from "@/modals/multicloud/MultiCloudVNConnectStatusModify.vue";
import MultiCloudGpuVnConnectionRetry from "@/modals/multicloud/gpu/MultiCloudGpuVnConnectionRetry.vue";
import MultiCloudGpuVnConnectionPendingDelete from "@/modals/multicloud/gpu/MultiCloudGpuVnConnectionPendingDelete.vue";
import type { Line } from "@/apis/CloudLineApi";
import MultiCloudGpuVnConnectionCreate from "./MultiCloudGpuVnConnectionCreate.vue";

type CloudVnGpuItem = CloudVnGpuList["cloudVnConnectsList"][0];

export default defineComponent({
  name: "MultiCloudGpuVnConnectionList",
  props: {
    /** クラウド回線SEQ */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    /** アクセス回線メニューの帯域 */
    accessLineMenuBandwidth: {
      type: Object as PropType<Line["bandwidth"]>,
      required: true,
    },
  },
  data() {
    return {
      vnConnectList: {} as CloudVnGpuList,
      vnConnects: {
        sortBy: { key: "wnumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "wnumber",
            title: "VNコネクト番号(Act/Sby)",
            width: "150px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnGpuItem) => {
              return item.cloudVnConnectAct.wnumber;
            },
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名(Act/Sby)",
            width: "140px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnGpuItem) => {
              return item.cloudVnConnectAct.vnConnectName;
            },
          },
          {
            key: "isVnConnectStatus",
            title: "有効/無効(Act/Sby)",
            width: "110px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnGpuItem) => {
              return this.$filter.enumTo(
                item.cloudVnConnectAct.isVnConnectStatus,
                "enable"
              );
            },
          },
          {
            key: "mNumber",
            title: "KDDI GPU Cloudご契約番号",
            width: "190px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnGpuItem) => {
              return item.cloudVnConnectCommon.mNumber;
            },
          },
          {
            key: "vpnVnCode",
            title: "接続先VPN/VNコード",
            width: "185px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnGpuItem) => {
              if (item.cloudVnConnectCommon.vpnVnCode.startsWith("D")) {
                return `${item.cloudVnConnectCommon.vpnVnCode}(
                  ${item.cloudVnConnectCommon.vnType}
                )`;
              }
              return item.cloudVnConnectCommon.vpnVnCode;
            },
          },
          {
            key: "bandwidth",
            title: "品目",
            width: "140px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnGpuItem) => {
              return this.$filter.bandwidthToBps(
                item.cloudVnConnectCommon.bandwidth
              );
            },
          },
          { key: "routingType", title: "ルーティング種別", width: "155px" },
          {
            key: "provStatus",
            title: "ステータス",
            width: "140px",
            formatter: (_v, _k, item: CloudVnGpuItem) => {
              return this.$filter.enumTo(
                item.cloudVnConnectCommon.provStatus,
                "gpuStatus"
              );
            },
          },
          { key: "description", title: "備考", width: "200px" },
        ] as AppTableHeader<CloudVnGpuList>[],
        selected: [] as CloudVnGpuList["cloudVnConnectsList"],
      },
      alertMsg: null as string | null,
      isLoaded: false,
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    /** 残りの設定可能帯域 */
    remainingBandwidth(): { value: number; unit: "MBPS" | "GBPS" } {
      const limitBandwidthMbps =
        (this.$filter.bandwidthToBps(this.vnConnectList.bandwidth) ?? 0) /
        1000 /
        1000;
      const sumBandwidthMbps = this.vnConnectList.cloudVnConnectsList.reduce(
        (a, e) =>
          a +
          (this.$filter.bandwidthToBps(e.cloudVnConnectCommon.bandwidth) ?? 0) /
            1000 /
            1000,
        0
      );
      const diffBandwidthMbps = limitBandwidthMbps - sumBandwidthMbps;
      if (diffBandwidthMbps >= 1000 || diffBandwidthMbps <= -1000) {
        return { value: diffBandwidthMbps / 1000, unit: "GBPS" };
      } else {
        return { value: diffBandwidthMbps, unit: "MBPS" };
      }
    },

    /** 再実行が必要なVNコネクトが存在する */
    retry(): boolean {
      return (
        this.vnConnectList.cloudVnConnectsList.some(
          (item) =>
            item.cloudVnConnectCommon.provStatus === "RETRYABLE" ||
            item.cloudVnConnectCommon.provStatus === "DELETE_RETRYABLE" ||
            item.cloudVnConnectCommon.provStatus === "CREATE_FAILED"
        ) ?? false
      );
    },

    /** 削除が必要なVNコネクトが存在する */
    pendingDelete(): boolean {
      return (
        this.vnConnectList.cloudVnConnectsList.some(
          (item) => item.cloudVnConnectCommon.provStatus === "PENDING_DELETE"
        ) ?? false
      );
    },

    /** エラー状態のVNコネクトが存在する */
    error(): boolean {
      return (
        this.vnConnectList.cloudVnConnectsList.some(
          (item) => item.cloudVnConnectCommon.provStatus === "FAILED"
        ) ?? false
      );
    },

    /** 再実行対象のW番号（RETRYABLE/DELETE_RETRYABLE/CREATE_FAILED の最初の1件） */
    wnumberSelected(): string {
      const item = this.vnConnectList.cloudVnConnectsList.find(
        (item) =>
          item.cloudVnConnectCommon.provStatus === "RETRYABLE" ||
          item.cloudVnConnectCommon.provStatus === "DELETE_RETRYABLE" ||
          item.cloudVnConnectCommon.provStatus === "CREATE_FAILED"
      );
      return item ? item.cloudVnConnectAct.wnumber : "";
    },

    /** エラー状態のW番号一覧 */
    wnumberSelectedError(): string[] {
      return this.vnConnectList.cloudVnConnectsList
        .filter((item) => item.cloudVnConnectCommon.provStatus === "FAILED")
        .map((item) => item.cloudVnConnectAct.wnumber);
    },

    /** 削除対象のW番号（仕様上1件のみ） */
    wnumberSelectedPendingDelete(): string {
      const item = this.vnConnectList.cloudVnConnectsList.find(
        (item) => item.cloudVnConnectCommon.provStatus === "PENDING_DELETE"
      );
      return item ? item.cloudVnConnectAct.wnumber : "";
    },
  },
  async mounted() {
    await this.load();
    if (this.retry && this.$service.permission.hasAuthority("CONFIG")) {
      this.retryVnConnect();
    } else if (
      this.pendingDelete &&
      this.$service.permission.hasAuthority("CONFIG")
    ) {
      this.pendingDeleteVnConnect();
    }
    this.isLoaded = true;
  },
  methods: {
    /** 読み込み */
    async load() {
      this.vnConnectList = await this.$api.cloudVnGpu.getVnConnects(
        this.cloudLineSeq
      );
    },

    /** VNコネクト追加 */
    async register() {
      this.alertMsg = null;
      await this.$modal.show(MultiCloudGpuVnConnectionCreate, {
        cloudLineSeq: this.cloudLineSeq,
        remainingBandwidth: this.remainingBandwidth, //表示のため変換せずに渡す
        cloudVnGpuKeyId: this.vnConnectList.cloudVnGpuKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** 有効/無効 */
    async modifyStatus(isEnabled: boolean) {
      if (this.vnConnects.selected.length === 0) {
        this.alertMsg = this.$filter.message("no_select_data");
        return;
      } else {
        this.alertMsg = null;
      }

      const wNumbers: string[] = await this.$modal.show(
        MultiCloudVNConnectStatusModify,
        {
          enable: isEnabled,
          vnConnects: this.vnConnects.selected,
        }
      );

      if (isEnabled) {
        await this.$api.cloudVnGpu.putEnableVnConnect({
          cloudVnConnectList: wNumbers,
          cloudVnGpuKeyId: this.vnConnectList.cloudVnGpuKeyId,
        });
      } else {
        await this.$api.cloudVnGpu.putDisableVnConnect({
          cloudVnConnectList: wNumbers,
          cloudVnGpuKeyId: this.vnConnectList.cloudVnGpuKeyId,
        });
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** テーブル選択 */
    async selectTable(wnumber: string) {
      await this.$modal.show(MultiCloudGpuVnConnectionReference, {
        wnumber: wnumber,
        cloudLineSeq: this.cloudLineSeq,
        remainingBandwidth: this.$filter.bandwidthToBps(
          this.remainingBandwidth
        )!,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** ステータスカラー */
    statusColor(status: string) {
      if (
        status === "RETRYABLE" ||
        status === "DELETE_RETRYABLE" ||
        status === "FAILED" ||
        status === "CREATE_FAILED" ||
        status === "PENDING_DELETE"
      ) {
        return "text-error";
      }
    },

    /** VNコネクト詳細表示 */
    async showConnectionDetails(entity: CloudVnGpuItem) {
      this.alertMsg = null;
      await this.$modal.show(MultiCloudGpuVnConnectionReference, {
        wnumber: entity.cloudVnConnectAct.wnumber,
        cloudLineSeq: this.cloudLineSeq,
        remainingBandwidth: this.$filter.bandwidthToBps(
          this.remainingBandwidth
        )!,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** VNコネクト再実行 */
    async retryVnConnect() {
      await this.$modal.show(MultiCloudGpuVnConnectionRetry, {
        cloudLineSeq: this.cloudLineSeq,
        vnConnectList: this.vnConnectList,
        cloudVnGpuKeyId: this.vnConnectList.cloudVnGpuKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** VNコネクト削除 */
    async pendingDeleteVnConnect() {
      await this.$modal.show(MultiCloudGpuVnConnectionPendingDelete, {
        cloudLineSeq: this.cloudLineSeq,
        vnConnectList: this.vnConnectList,
        cloudVnGpuKeyId: this.vnConnectList.cloudVnGpuKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<CloudVnGpuList>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
